import type { KeyboardEvent } from 'react';
import { useCallback, useRef } from 'react';

import { cn } from '#/utils/cn';

import { Chip } from './Chip';

export interface TabItem<T = string> {
  id: T;
  label: string;
  group?: string;
  controls?: string;
}

export interface TabsProps<T> {
  tabs: TabItem<T>[];
  activeTab: T;
  onChange: (id: T) => void;
  variant?: 'default' | 'chip';
  'aria-label'?: string;
}

export function Tabs<T extends string>({
  tabs,
  activeTab,
  onChange,
  variant = 'default',
  'aria-label': ariaLabel,
}: TabsProps<T>) {
  const tablistRef = useRef<HTMLDivElement>(null);

  const focusTab = useCallback((id: T) => {
    tablistRef.current
      ?.querySelector<HTMLButtonElement>(`#${CSS.escape(id)}`)
      ?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      const count = tabs.length;

      if (count === 0) {
        return;
      }

      let nextIndex: number | null = null;

      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = currentIndex === 0 ? count - 1 : currentIndex - 1;
          break;
        case 'ArrowRight':
          nextIndex = currentIndex === count - 1 ? 0 : currentIndex + 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = count - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      const nextTab = tabs[nextIndex];

      if (!nextTab) {
        return;
      }

      onChange(nextTab.id);
      focusTab(nextTab.id);
    },
    [tabs, onChange, focusTab],
  );

  return (
    <div
      ref={tablistRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex flex-wrap items-center gap-4 sm:flex-nowrap')}
    >
      {tabs.map((tab, index) => {
        const prevTab = tabs[index - 1];
        const showDivider = index > 0 && prevTab?.group !== tab.group;
        const isActive = activeTab === tab.id;

        return (
          <div key={tab.id} className="flex items-center">
            {showDivider && (
              <div
                className="bg-gray-40 ms-4 me-8 h-16 w-px"
                aria-hidden="true"
              />
            )}
            {variant === 'chip' ? (
              <Chip
                text={tab.label}
                style="outlined"
                state={isActive ? 'active' : 'default'}
                onClick={() => onChange(tab.id)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                id={tab.id}
                role="tab"
                tabIndex={isActive ? 0 : -1}
                aria-selected={isActive}
                aria-controls={tab.controls}
              />
            ) : (
              <button
                type="button"
                role="tab"
                id={tab.id}
                tabIndex={isActive ? 0 : -1}
                aria-selected={isActive}
                aria-controls={tab.controls}
                onClick={() => onChange(tab.id)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={cn(
                  'text-static-14 rounded px-8 py-2 font-medium transition-colors',
                  isActive
                    ? 'text-tab-fg-active bg-tab-bg-active'
                    : 'text-tab-fg hover:text-tab-fg-hover',
                )}
              >
                {tab.label}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
